import { Button } from '@/components/ui/design-system';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { H4, P3 } from './ui/text';

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  isLoading?: boolean;
}

function ConfirmationDialogComponent({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
}: ConfirmationDialogProps) {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
        showCloseIcon={false}
      >
        <DialogHeader className="gap-1 px-4 py-3">
          <DialogTitle>
            <H4 className="text-primary-100">{title}</H4>
          </DialogTitle>
          {description && (
            <DialogDescription>
              <P3 className="text-primary-60">{description}</P3>
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            size="sm"
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmationDialogComponent;

// Legacy named export for backwards compatibility with old API
interface LegacyConfirmationDialogProps {
  open: boolean;
  onOpenChange?: (open?: boolean) => void;
  title: string;
  description?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  confirmLabel?: string;
  confirmButtonStyle?: string;
  cancelButtonStyle?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  variant?: 'destructive' | 'success';
}

export const ConfirmationDialog = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  variant = 'destructive',
  children,
}: LegacyConfirmationDialogProps) => {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  const handleConfirm = async () => {
    await onConfirm?.();
    onOpenChange?.(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange?.(o);
      }}
    >
      <DialogContent
        className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
        showCloseIcon={false}
      >
        <DialogHeader className="gap-1 px-4 py-3">
          <DialogTitle>
            <H4 className="text-primary-100">{title}</H4>
          </DialogTitle>
          {description && (
            <DialogDescription>
              <P3 className="text-primary-60">{description}</P3>
            </DialogDescription>
          )}
        </DialogHeader>
        {children && <div className="flex flex-col gap-3 px-4 py-3">{children}</div>}
        <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'success' ? 'primary' : 'destructive'}
            size="sm"
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
